import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { ArrowLeft, ArrowRight, ChevronDown, Zap } from "lucide-react";
import { useLang } from "@/i18n";

const StatNumber = ({ value, testid }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const match = value.match(/^(\+?)(\d+)(.*)$/);
  const [display, setDisplay] = useState(match ? `${match[1]}0${match[3]}` : value);

  useEffect(() => {
    if (!inView || !match) return;
    const end = parseInt(match[2], 10);
    let raf;
    const timer = setTimeout(() => {
      const start = performance.now();
      const duration = 1800;
      const tick = (now) => {
        const p = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - p, 3);
        setDisplay(`${match[1]}${Math.max(0, Math.round(end * eased))}${match[3]}`);
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, 1150);
    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(raf);
    };
  }, [inView]);

  return <span ref={ref} data-testid={testid}>{display}</span>;
};

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const { t, dir } = useLang();
  const { scrollY } = useScroll();
  const yLogo = useTransform(scrollY, [0, 700], [0, 140]);
  const opacityLogo = useTransform(scrollY, [0, 500], [0.22, 0]);
  const Arrow = dir === "rtl" ? ArrowLeft : ArrowRight;

  return (
    <section id="top" data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1694018359679-49465b4c0d61?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxzYXVkaSUyMHZpc2lvbiUyMDIwMzAlMjBtb2Rlcm4lMjBidWlsZGluZ3xlbnwwfHx8fDE3ODYyOTkwOTR8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="w-full h-full object-cover opacity-40 saturate-[0.6]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-900/70 via-navy-900/85 to-navy-900" />
        <div className="absolute inset-0 bg-gradient-to-l from-navy-900/80 via-transparent to-navy-900/40" />
      </div>

      <motion.img
        src="/logo.png"
        alt=""
        style={{ y: yLogo, opacity: opacityLogo }}
        className="pointer-events-none select-none absolute -end-10 top-24 w-[380px] lg:w-[480px] animate-float-slow"
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-20 w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gold/30 bg-gold/10 text-gold text-xs md:text-sm font-medium mb-8"
          data-testid="hero-overline"
        >
          <Zap size={14} className="fill-gold" />
          {t("hero_overline")}
        </motion.div>

        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-7xl leading-[1.15] tracking-tight max-w-4xl" data-testid="hero-headline">
          <MaskedLine delay={0.25}>{t("hero_l1")}</MaskedLine>
          <MaskedLine delay={0.4} className="gold-text">{t("hero_l2")}</MaskedLine>
          <MaskedLine delay={0.55}>{t("hero_l3")}</MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.75 }}
          className="mt-8 max-w-2xl text-white/70 text-base md:text-lg leading-relaxed"
          data-testid="hero-subtext"
        >
          {t("hero_sub")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a href="#contact" data-testid="hero-cta-primary"
            className="group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-gold text-navy-900 font-bold hover:bg-gold-light transition-colors duration-300 shadow-xl shadow-gold/20">
            {t("hero_cta1")}
            <Arrow size={18} className="transition-transform duration-300 group-hover:-translate-x-1 rtl:group-hover:-translate-x-1 ltr:group-hover:translate-x-1" />
          </a>
          <a href="#services" data-testid="hero-cta-secondary"
            className="inline-flex items-center gap-3 px-8 py-4 rounded-full border border-white/20 text-white font-semibold hover:border-gold/60 hover:text-gold transition-colors duration-300">
            {t("hero_cta2")}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.1 }}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 rounded-2xl overflow-hidden max-w-4xl"
          data-testid="hero-stats"
        >
          {t("stats").map((s) => (
            <div key={s.l} className="bg-navy-800/80 backdrop-blur px-6 py-6" data-testid={`stat-${s.n}`}>
              <div className="font-display text-3xl md:text-4xl font-extrabold gold-text"><StatNumber value={s.n} testid={`stat-number-${s.l}`} /></div>
              <div className="mt-1 text-xs md:text-sm text-white/60">{s.l}</div>
            </div>
          ))}
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/40 text-xs"
      >
        {t("hero_scroll")}
        <ChevronDown size={16} className="animate-bounce" />
      </motion.div>
    </section>
  );
}
